import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import PaginationControls from '../PaginationControls';
import AgGridTable from '../AgGridTable';

const AuditLogs = ({
  resourceId = null,
  resourceType = 'case', // 'case', 'prompt', 'user', or 'general'
  userType = null, // Required when resourceType is 'user' (ankr, healthcare, patient, sales, provider)
  apiFilters = {},
  title = 'Audit Trail',
  showHeader = true,
  maxHeight = null,
  pageSize = 50
}) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: pageSize,
    has_next: false,
    has_previous: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Format timestamp for display
  const formatTimestamp = (timestampString) => {
    if (!timestampString) return 'N/A';
    const date = new Date(timestampString);
    return date.toLocaleString();
  };

  // Fetch audit logs based on resource type and filters
  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const filters = {
          page: currentPage,
          page_size: pageSize,
          ...apiFilters
        };

        let response;
        if (resourceType === 'case' && resourceId) {
          // Use case-specific audit logs endpoint
          response = await adminApi.getCaseAuditLogs(resourceId, filters);
        } else if (resourceType === 'user' && resourceId && userType) {
          // Use user-specific audit logs endpoint
          response = await adminApi.getUserAuditLogs(userType, resourceId, filters);
        } else {
          // Use general audit logs endpoint with filters
          if (resourceType === 'prompt' && resourceId) {
            // Filter by specific prompt resource
            filters.requested_resource = `llm_prompt_${resourceId}`;
          }
          response = await adminApi.getAuditLogs(filters);
        }
        
        if (response.data && response.data.success) {
          setAuditLogs(response.data.data || []);
          setPagination(prev => ({ ...prev, ...response.data.pagination }));
        } else {
          setError(response.data?.message || 'Failed to fetch audit logs');
        }
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        setError(err.response?.data?.detail || err.message || 'An error occurred while fetching audit logs');
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, resourceType, userType, currentPage, pageSize, JSON.stringify(apiFilters)]);

  // Pagination handlers
  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handlePreviousPage = () => {
    if (pagination.has_previous) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (pagination.has_next) setCurrentPage(currentPage + 1);
  };

  const containerStyle = maxHeight ? { maxHeight, overflowY: 'auto' } : {};

  return (
    <div className="audit-logs-component" style={containerStyle}>
      {showHeader && (
        <div className="card-title text-primary d-flex justify-content-between align-items-center mb-3">
            {/* <i className="fas fa-history me-2"></i> */}
            {title}
          {auditLogs.length > 0 && (
            <small className="text-muted">
              {pagination.total_count} total records
            </small>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center my-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 small">Loading audit logs...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger small">
          <i className="fas fa-exclamation-triangle me-1"></i>
          {error}
        </div>
      ) : auditLogs.length === 0 ? (
        <div className="alert alert-light small text-center m-2">
          <i className="fas fa-info-circle me-1"></i>
          No audit logs found
        </div>
      ) : (
        <>
          <AgGridTable
            rowData={auditLogs}
            height="100%"
            columnDefs={[
              {
                headerName: 'Timestamp',
                field: 'timestamp',
                valueGetter: (p) => p.data?.timestamp ? formatTimestamp(p.data.timestamp) : '',
                cellRenderer: (params) => {
                  if (!params.data) return null;
                  return <small>{formatTimestamp(params.data.timestamp)}</small>;
                }
              },
              {
                headerName: 'Action',
                field: 'access_type',
                cellRenderer: (params) => {
                  if (!params.data) return null;
                  const log = params.data;
                  const badgeClass = log.access_type?.includes('create') ? 'bg-success' :
                    log.access_type?.includes('update') ? 'bg-primary' :
                    log.access_type?.includes('delete') ? 'bg-danger' :
                    log.access_type?.includes('failed') || log.result === 'failure' ? 'bg-danger' :
                    'bg-secondary';
                  return (
                    <small>
                      <span className={`badge badge-sm ${badgeClass}`}>
                        {log.access_type}
                      </span>
                    </small>
                  );
                }
              },
              {
                headerName: 'Result',
                field: 'result',
                cellRenderer: (params) => {
                  if (!params.data) return null;
                  const log = params.data;
                  const badgeClass = log.result === 'success' ? 'bg-success' : 
                    log.result === 'failure' ? 'bg-danger' : 'bg-warning';
                  return (
                    <small>
                      <span className={`badge badge-sm ${badgeClass}`}>
                        {log.result}
                      </span>
                    </small>
                  );
                }
              },
              {
                headerName: 'User',
                field: 'django_user_email',
                valueGetter: (p) => {
                  if (!p.data) return 'Unknown';
                  // Prefer email, fallback to username, then token ID
                  if (p.data.django_user_email) return p.data.django_user_email;
                  if (p.data.django_user_username) return p.data.django_user_username;
                  if (p.data.authentication_info?.api_token_id) return `Token: ${p.data.authentication_info.api_token_id.substring(0, 8)}...`;
                  return 'Unknown';
                },
                cellRenderer: (params) => {
                  if (!params.data) return null;
                  const log = params.data;

                  // Display user email/name if available
                  if (log.django_user_email || log.django_user_full_name) {
                    return (
                      <small className="d-flex flex-column">
                        <span className="text-primary">
                          <i className="fas fa-user me-1"></i>
                          {log.django_user_email || log.django_user_username}
                        </span>
                        {log.django_user_full_name && (
                          <span className="text-muted" style={{ fontSize: '0.85em' }}>
                            {log.django_user_full_name}
                          </span>
                        )}
                      </small>
                    );
                  }

                  // Fallback to token ID if no user info
                  if (log.authentication_info?.api_token_id) {
                    return (
                      <small className="text-muted">
                        <i className="fas fa-key me-1"></i>
                        Token: {log.authentication_info.api_token_id.substring(0, 8)}...
                      </small>
                    );
                  }

                  return <small className="text-muted">Unknown</small>;
                }
              },
              // {
              //   headerName: 'IP Address',
              //   field: 'client_ip_address',
              //   valueGetter: (p) => p.data?.client_ip_address || 'N/A',
              //   cellRenderer: (params) => {
              //     if (!params.data) return null;
              //     return <small>{params.data.client_ip_address || 'N/A'}</small>;
              //   }
              // },
              {
                headerName: 'Details',
                field: 'details',
                width: 500,
                filter: false,
                cellStyle: { overflow: 'visible' },
                cellRenderer: (params) => {
                  if (!params.data) return null;
                  const log = params.data;
                  const detailsData = JSON.stringify({
                    requested_resource: log.requested_resource,
                    request_method: log.request_method,
                    http_status_code: log.http_status_code,
                    additional_info: log.additional_info,
                    authentication_info: log.authentication_info,
                    query_parameters: log.query_parameters,
                    error_details: log.error_details,
                    server_details: log.server_details,
                    user_agent: log.user_agent,
                    latency: log.latency
                  }, null, 2);
                  return (
                    <div style={{ height: '300px', overflowY: 'auto', overflowX: 'auto', padding: '4px 0' }}>
                    <div className="d-flex justify-content-end mb-1">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigator.clipboard.writeText(detailsData)}
                        title="Copy details to clipboard"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                    <small>
                      {log.requested_resource && (
                        <p><strong>Resource:</strong> {log.requested_resource}</p>
                      )}
                      {log.request_method && (
                        <p><strong>Method:</strong> {log.request_method}</p>
                      )}
                      {log.http_status_code && (
                        <p><strong>Status:</strong> {log.http_status_code}</p>
                      )}
                      <p><strong>IP Address:</strong> {params.data.client_ip_address || 'N/A'}</p>
                      {/* LLM Prompt specific details */}
                      {log.additional_info && (
                        <div>
                          {log.additional_info.prompt_name && (
                            <p><strong>Prompt:</strong> {log.additional_info.prompt_name}</p>
                          )}
                          {log.additional_info.prompt_type && (
                            <p><strong>Type:</strong> {log.additional_info.prompt_type}</p>
                          )}
                          {log.additional_info.health_system && (
                            <p><strong>Health System:</strong> {log.additional_info.health_system}</p>
                          )}
                          {log.additional_info.specialty && (
                            <p><strong>Specialty:</strong> {log.additional_info.specialty}</p>
                          )}
                          {log.additional_info.fields_updated && (
                            <p><strong>Updated:</strong> {log.additional_info.fields_updated.join(', ')}</p>
                          )}
                          {log.additional_info.action && (
                            <p><strong>Action:</strong> {log.additional_info.action}</p>
                          )}
                        </div>
                      )}

                      {/* Authentication details */}
                      {log.authentication_info && Object.keys(log.authentication_info).length > 0 && (
                        <p>
                          <strong>Auth:</strong>
                          <ul className="list-unstyled ms-2 small">
                            {Object.entries(log.authentication_info).map(([key, value]) => (
                              <li key={key}>
                                <strong>{key}:</strong> {
                                  key === 'api_token_id' && typeof value === 'string' ? 
                                    `${value.substring(0, 8)}...` : 
                                    JSON.stringify(value)
                                }
                              </li>
                            ))}
                          </ul>
                        </p>
                      )}

                      {/* Query parameters */}
                      {log.query_parameters && Object.keys(log.query_parameters).length > 0 && (
                        <div>
                          <strong>Query Params:</strong>
                          <ul className="list-unstyled ms-2 small">
                            {Object.entries(log.query_parameters).map(([key, value]) => (
                              <li key={key}>
                                <strong>{key}:</strong> {JSON.stringify(value)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Error details */}
                      {log.error_details && Object.keys(log.error_details).length > 0 && (
                        <div>
                          <strong className="text-danger">Error:</strong>
                          <p className="text-danger small">
                            {log.error_details.form_assignment_results ? (
                              <ul className="list-unstyled ms-2">
                                {log.error_details.form_assignment_results.map((form, index) => (
                                  <li key={index}>
                                    Form: {form.name} (ID: {form.form_id}, UUID: {form.UUID})
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                                {typeof log.error_details === 'string' ? 
                                  log.error_details : 
                                  JSON.stringify(log.error_details, null, 1)
                                }
                              </p>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Server details - Enhanced for LLM Prompts */}
                      {log.server_details && Object.keys(log.server_details).length > 0 && (
                        <div>
                          {/* LLM Prompt CREATE */}
                          {log.access_type === 'llm_prompt_create' && log.server_details.prompt_name && (
                            <div>
                              <strong className="text-success">Created Prompt:</strong>
                              <ul className="list-unstyled ms-2 small">
                                <li><strong>Name:</strong> {log.server_details.prompt_name}</li>
                                {log.server_details.prompt_id && <li><strong>ID:</strong> {log.server_details.prompt_id}</li>}
                                {log.server_details.description && <li><strong>Description:</strong> {log.server_details.description}</li>}
                                {log.server_details.prompt_type && <li><strong>Type:</strong> <span className="badge bg-primary">{log.server_details.prompt_type}</span></li>}
                                {log.server_details.health_system && <li><strong>Health System:</strong> {log.server_details.health_system}</li>}
                                {log.server_details.specialty && <li><strong>Specialty:</strong> {log.server_details.specialty}</li>}
                                {log.server_details.is_active !== undefined && <li><strong>Active:</strong> <span className={`badge ${log.server_details.is_active ? 'bg-success' : 'bg-secondary'}`}>{log.server_details.is_active ? 'Yes' : 'No'}</span></li>}
                                {log.server_details.version && <li><strong>Version:</strong> {log.server_details.version}</li>}
                                {log.server_details.content_length && <li><strong>Content Length:</strong> {log.server_details.content_length} chars</li>}
                                {log.server_details.content_preview && (
                                  <li>
                                    <strong>Content Preview:</strong>
                                    <div className="text-muted small font-monospace border rounded p-1 mt-1" style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '0.75rem' }}>
                                      {log.server_details.content_preview}
                                    </div>
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* LLM Prompt UPDATE */}
                          {log.access_type === 'llm_prompt_update' && log.server_details.prompt_name && (
                            <div>
                              <strong className="text-primary">Updated Prompt:</strong>
                              <ul className="list-unstyled ms-2 small">
                                <li><strong>Name:</strong> {log.server_details.prompt_name}</li>
                                {log.server_details.prompt_id && <li><strong>ID:</strong> {log.server_details.prompt_id}</li>}
                                {log.server_details.fields_updated && log.server_details.fields_updated.length > 0 && (
                                  <li><strong>Fields Submitted:</strong> {log.server_details.fields_updated.join(', ')}</li>
                                )}
                                {log.server_details.fields_changed && log.server_details.fields_changed.length > 0 && (
                                  <li><strong>Fields Actually Changed:</strong> <span className="badge bg-warning text-dark">{log.server_details.fields_changed.join(', ')}</span></li>
                                )}
                              </ul>

                              {/* Show detailed changes */}
                              {log.server_details.changes && Object.keys(log.server_details.changes).length > 0 && (
                                <div className="mt-2">
                                  <strong>Changes:</strong>
                                  <p className="border rounded p-2 mt-1" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.75rem' }}>
                                    {Object.entries(log.server_details.changes).map(([field, change]) => (
                                      <div key={field} className="mb-2">
                                        <strong className="text-info">{field}:</strong>
                                        {field === 'content' ? (
                                          <div>
                                            <div className="text-danger small">
                                              <strong>Old:</strong> {change.old_length} chars
                                              <div className="font-monospace border rounded p-1 mt-1" style={{ maxHeight: '60px', overflowY: 'auto', backgroundColor: '#fff3cd' }}>
                                                {change.old_preview}
                                              </div>
                                            </div>
                                            <div className="text-success small mt-1">
                                              <strong>New:</strong> {change.new_length} chars
                                              <div className="font-monospace border rounded p-1 mt-1" style={{ maxHeight: '60px', overflowY: 'auto', backgroundColor: '#d1e7dd' }}>
                                                {change.new_preview}
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <span className="text-danger"><del>{JSON.stringify(change.old)}</del></span>
                                            {' → '}
                                            <span className="text-success"><strong>{JSON.stringify(change.new)}</strong></span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* LLM Prompt DELETE */}
                          {log.access_type === 'llm_prompt_delete' && log.server_details.deleted_prompt && (
                            <div>
                              <strong className="text-danger">Deleted Prompt:</strong>
                              <ul className="list-unstyled ms-2 small">
                                <li><strong>Name:</strong> {log.server_details.deleted_prompt.name}</li>
                                <li><strong>ID:</strong> {log.server_details.deleted_prompt.id}</li>
                                {log.server_details.deleted_prompt.description && <li><strong>Description:</strong> {log.server_details.deleted_prompt.description}</li>}
                                {log.server_details.deleted_prompt.prompt_type && <li><strong>Type:</strong> <span className="badge bg-secondary">{log.server_details.deleted_prompt.prompt_type}</span></li>}
                                {log.server_details.deleted_prompt.health_system && <li><strong>Health System:</strong> {log.server_details.deleted_prompt.health_system}</li>}
                                {log.server_details.deleted_prompt.specialty && <li><strong>Specialty:</strong> {log.server_details.deleted_prompt.specialty}</li>}
                                {log.server_details.deleted_prompt.version && <li><strong>Version:</strong> {log.server_details.deleted_prompt.version}</li>}
                                {log.server_details.deleted_prompt.created_by && <li><strong>Created By:</strong> {log.server_details.deleted_prompt.created_by}</li>}
                                {log.server_details.deleted_prompt.created_at && <li><strong>Created:</strong> {formatTimestamp(log.server_details.deleted_prompt.created_at)}</li>}
                                {log.server_details.deleted_prompt.content_length && <li><strong>Content Length:</strong> {log.server_details.deleted_prompt.content_length} chars</li>}
                                {log.server_details.deleted_prompt.content_preview && (
                                  <li>
                                    <strong>Content Preview (first 500 chars):</strong>
                                    <div className="text-muted small font-monospace border rounded p-1 mt-1" style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.75rem', backgroundColor: '#f8d7da' }}>
                                      {log.server_details.deleted_prompt.content_preview}
                                    </div>
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Fallback for other server details */}
                          {!['llm_prompt_create', 'llm_prompt_update', 'llm_prompt_delete'].includes(log.access_type) && (
                            <div>
                              <strong>Server Details:</strong>
                              <p className="small ms-2" style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                                {typeof log.server_details === 'string' ?
                                  log.server_details :
                                  JSON.stringify(log.server_details, null, 1)
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {log.user_agent && (
                        <div><strong>User Agent:</strong> 
                          <p className="small text-muted" style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                            {log.user_agent}
                          </p>
                        </div>
                      )}
                      
                      {log.latency && (
                        <div><strong>Latency:</strong> {log.latency} ms</div>
                      )}
                    </small>
                    </div>
                  );
                }
              }
            ]}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
            }}
            gridOptions={{
              domLayout: 'autoHeight',
              suppressHorizontalScroll: false,
              autoSizeStrategy: { type: 'fitCellContents' },
              rowHeight: 320
            }}
            getRowId={(params) => `${params.data.id}`}
          />

          {/* Pagination Controls */}
          {/* {pagination.total_pages > 1 && (
            <PaginationControls
              pagination={pagination}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              className="mt-3"
            />
          )} */}
        </>
      )}
    </div>
  );
};

export default AuditLogs;